import { ChangeEvent, FormEvent, MouseEvent, useEffect, useState } from "react";

import { InfiniteDataType, toDateString } from "~/libs";

import {
  useDateRange,
  useInput,
  useApisManagersInsightPostsIndex,
  useApisMembersInsightPostSearchItemsIndex,
  usePostSortType,
  useArray,
  useMultiDivisionMultiSectionDropdown,
  useProvidersCurrentEmployee,
  useProvidersEmployeeLayout,
  useCompleteStatusDropdown,
  useBoolean,
  useMultiJobCategoryMultiJobCategoryPhaseDropdown,
} from "~/hooks";

import {
  ApiManagersInsightPostsIndexRequestType,
  InsightPostType,
  ApiManagersInsightPostsIndexResponseType,
  OptionType,
  SortType,
  ChildType,
  ParentType,
  SingleValueType,
} from "~/domains";

import { RangeDatePropsType } from "~/ui";

type ReturnType = {
  insightPosts: InsightPostType[];
  data?: InfiniteDataType<ApiManagersInsightPostsIndexResponseType>;
  hasNextPage?: boolean;
  isLoading: boolean;
  keyword: string;
  selectableDropdownHasCommentIds: OptionType[];
  selectedDropdownHasCommentId?: SingleValueType<OptionType>;
  selectableDropdownHasBookmarkIds: OptionType[];
  selectedDropdownHasBookmarkId?: SingleValueType<OptionType>;
  selectableDropdownSharedIds: OptionType[];
  selectedDropdownSharedId?: SingleValueType<OptionType>;
  startDate: Date | null;
  endDate: Date | null;
  sort: SortType;
  isBelongingEmployee: boolean;
  selectableJobCategories: ParentType[];
  selectedJobCategories: ParentType[];
  optionSelectableJobCategoryPhases: ChildType[];
  optionSelectedJobCategoryPhases: ChildType[];
  selectedDropdownEmployees: ParentType[];
  selectableDropdownEmployees: ParentType[];
  selectableDropdownDivisions: ParentType[];
  selectedDropdownDivisions: ParentType[];
  selectableDropdownSections: ChildType[];
  selectedDropdownSections: ChildType[];
  selectedDropdownArchivedEmployees: ParentType[];
  selectableDropdownArchivedEmployees: ParentType[];
  onDivisionChange: (value: ParentType[]) => void;
  onSectionChange: (value: ChildType[]) => void;
  onEmployeesChange: (value: ParentType[]) => void;
  onArchivedEmployeesChange: (value: ParentType[]) => void;
  onChangeDateRange: (value: RangeDatePropsType) => void;
  onChangeJobCategoryPhases: (newValue: ChildType[]) => void;
  onChangeJobCategories: (newValue: ParentType[]) => void;
  deleteIndexInsightPost: (index: number) => void;
  updateIndexInsightPost: (newItem: InsightPostType, index: number) => void;
  addInsightPost: (newItem: InsightPostType) => void;
  setSort: (val: SortType) => void;
  fetchNextPage: () => void;
  refetch: () => void;
  onChangeIsBelongingEmployee: (e: ChangeEvent<HTMLInputElement>) => void;
  onChangeKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onChangeDropdownHasCommentId: (value: SingleValueType<OptionType>) => void;
  onChangeDropdownHasBookmarkId: (value: SingleValueType<OptionType>) => void;
  onChangeDropdownSharedId: (value: SingleValueType<OptionType>) => void;
  onConditionReset: (e: MouseEvent<HTMLButtonElement>) => void;
};

export const useSearchManagersInsightPosts = (): ReturnType => {
  const { setFalseRightSidebarMain } = useProvidersEmployeeLayout();
  const { currentEmployee } = useProvidersCurrentEmployee();
  const { items, setItems, deleteIndexItem, updateIndexItem, unshiftItem } =
    useArray<InsightPostType>([]);
  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
    selectableEmployees,
    selectedEmployees,
    onEmployeesChange,
    selectableArchivedEmployees,
    selectedArchivedEmployees,
    onArchivedEmployeesChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions,
    selectableSections: currentEmployee?.businessSections,
    isSelectEmployee: true,
  });
  const {
    isChecked: isBelongingEmployee,
    onChange: onChangeIsBelongingEmployee,
    setFalse: setBelongingEmployeeFalse,
  } = useBoolean(true);

  const { data: fetchData } = useApisMembersInsightPostSearchItemsIndex();

  const {
    selectableJobCategories,
    selectedJobCategories,
    onChangeJobCategories,
    optionSelectableJobCategoryPhases,
    optionSelectedJobCategoryPhases,
    onChangeJobCategoryPhases,
  } = useMultiJobCategoryMultiJobCategoryPhaseDropdown({
    selectableJobCategories: fetchData?.jobCategories,
    selectableJobCategoryPhases: fetchData?.jobCategoryPhases,
    withAllJobCategoryPhase: true,
  });

  const [{ value: keyword, onChange: onChangeKeyword }, setKeyword] =
    useInput("");
  const [
    {
      statusValue: hasComment,
      selectedDropdownCompleteStatusId: selectedDropdownHasCommentId,
      selectableDropdownCompleteStatusIds: selectableDropdownHasCommentIds,
      onChangeDropdownCompleteStatusId: onChangeDropdownHasCommentId,
    },
  ] = useCompleteStatusDropdown({
    initialStatusNames: {
      true: "リプライあり",
      false: "リプライなし",
    },
  });
  const [
    {
      statusValue: hasBookmark,
      selectedDropdownCompleteStatusId: selectedDropdownHasBookmarkId,
      selectableDropdownCompleteStatusIds: selectableDropdownHasBookmarkIds,
      onChangeDropdownCompleteStatusId: onChangeDropdownHasBookmarkId,
    },
  ] = useCompleteStatusDropdown({
    initialStatusNames: {
      true: "ブックマークあり",
      false: "ブックマークなし",
    },
  });
  const [
    {
      statusValue: shared,
      selectedDropdownCompleteStatusId: selectedDropdownSharedId,
      selectableDropdownCompleteStatusIds: selectableDropdownSharedIds,
      onChangeDropdownCompleteStatusId: onChangeDropdownSharedId,
    },
  ] = useCompleteStatusDropdown({
    initialStatusNames: {
      true: "シェアあり",
      false: "シェアなし",
    },
  });
  const [{ startDate, endDate, onChange: onChangeDateRange }] = useDateRange();
  const { sort, snakeKeyValue, setSort } = usePostSortType();

  const [params, setParams] = useState<ApiManagersInsightPostsIndexRequestType>(
    {
      postDateFrom: startDate ? toDateString(startDate) : "",
      postDateTo: endDate ? toDateString(endDate) : "",
      sortType: snakeKeyValue,
      isBelongingEmployee,
    },
  );

  const { data, fetchNextPage, hasNextPage, refetch, isFetching } =
    useApisManagersInsightPostsIndex({
      params,
      config: {
        onSuccess: (data) => {
          setItems(data.pages.map((page) => page.insightPosts).flat());
        },
      },
    });

  const onSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setParams({
      keyword,
      hasComment,
      hasBookmark,
      shared,
      sortType: snakeKeyValue,
      isBelongingEmployee,
      employeeIds: [
        ...selectedEmployees.map((ele) => ele.id),
        ...selectedArchivedEmployees.map((ele) => ele.id),
      ].map((ele) => ele),
      businessDivisionIds: selectedDivisions?.map((division) => division.id),
      businessSectionIds: optionSelectedSections?.map((section) => section.id),
      jobCategoryIds: selectedJobCategories?.map(
        (jobCategory) => jobCategory.id,
      ),
      jobCategoryPhaseIds: optionSelectedJobCategoryPhases?.map(
        (ele) => ele.id,
      ),
      postDateFrom: startDate ? toDateString(startDate) : "",
      postDateTo: endDate ? toDateString(endDate) : "",
    });
    setFalseRightSidebarMain();
  };

  const onConditionReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setKeyword("");
    onChangeDropdownHasBookmarkId(null);
    onChangeDropdownHasCommentId(null);
    onChangeDropdownSharedId(null);
    onChangeDateRange([null, null]);
    onEmployeesChange([]);
    onArchivedEmployeesChange([]);
    onDivisionChange([]);
    onSectionChange([]);
    onChangeJobCategories([]);
    onChangeJobCategoryPhases([]);
    setParams({ sortType: snakeKeyValue });
    setFalseRightSidebarMain();
    setBelongingEmployeeFalse();
  };

  useEffect(() => {
    setParams((prevParams) => ({
      ...prevParams,
      sortType: snakeKeyValue,
    }));
  }, [snakeKeyValue]);

  return {
    insightPosts: items,
    data,
    isLoading: isFetching,
    hasNextPage,
    keyword,
    selectableDropdownHasCommentIds,
    selectedDropdownHasCommentId,
    selectableDropdownHasBookmarkIds,
    selectedDropdownHasBookmarkId,
    selectableDropdownSharedIds,
    selectedDropdownSharedId,
    startDate,
    endDate,
    sort,
    isBelongingEmployee,
    selectableJobCategories,
    selectedJobCategories,
    optionSelectableJobCategoryPhases,
    optionSelectedJobCategoryPhases,
    selectedDropdownEmployees: selectedEmployees,
    selectableDropdownEmployees: selectableEmployees,
    selectableDropdownDivisions: selectableDivisions,
    selectedDropdownDivisions: selectedDivisions,
    selectableDropdownSections: optionSelectableSections,
    selectedDropdownSections: optionSelectedSections,
    selectableDropdownArchivedEmployees: selectableArchivedEmployees,
    selectedDropdownArchivedEmployees: selectedArchivedEmployees,
    onDivisionChange,
    onSectionChange,
    onEmployeesChange,
    onArchivedEmployeesChange,
    addInsightPost: unshiftItem,
    updateIndexInsightPost: updateIndexItem,
    deleteIndexInsightPost: deleteIndexItem,
    onChangeDateRange,
    onChangeJobCategoryPhases,
    onChangeJobCategories,
    setSort,
    fetchNextPage,
    refetch,
    onChangeIsBelongingEmployee,
    onChangeKeyword,
    onSearchSubmit,
    onChangeDropdownHasCommentId,
    onChangeDropdownHasBookmarkId,
    onChangeDropdownSharedId,
    onConditionReset,
  };
};
